import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Checkbox,
  Collapse,
  IconButton,
  TableSortLabel,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ChargingStationIcon from '@mui/icons-material/ChargingStation';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../../theme';
import useApi from '../../../shared/hooks/useApi';
import { useSimulation } from '../../context/SimulationContext';
import { exportSimulationsToExcel } from '../../helpers/exportHelpers';

export default function BESSRevenueOverview() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { apiGet, apiPost } = useApi();
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [simulationKpis, setSimulationKpis] = useState({});
  const [loadingKpis, setLoadingKpis] = useState({});
  const [expandedConfigs, setExpandedConfigs] = useState([]);

  // Get simulations from context - no need to fetch here anymore!
  const { simulations, isLoading: isLoadingSimulations, error: simulationsError } = useSimulation();

  // Fetch KPI data when a row is expanded
  const fetchSimulationKpis = async (eventId) => {
    if (simulationKpis[eventId]) {
      // Already fetched
      return;
    }
    setLoadingKpis(prev => ({ ...prev, [eventId]: true }));

    try {
      // Extract s3_key from simulation
      const selectedSimulation = simulations.find(sim => sim.event_id === eventId);
      let s3_key = null;
      if (selectedSimulation && selectedSimulation.message) {
          try {
              // Remove BOM and other invisible characters
              let cleanMessage = selectedSimulation.message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
              // Handle NaN values in JSON before parsing
              cleanMessage = cleanMessage.replace(/:\s*NaN\s*([,}])/g, ': null$1');
              let messageData = JSON.parse(cleanMessage);
              s3_key = messageData.s3_key;
          } catch (error) {
              console.error("Error parsing simulation message:", error);
          }
      }

      // Build query params as object
      const queryParams = {
        simulation_id: eventId
      };
      if (s3_key) {
        queryParams.s3_key = s3_key;
      }

      let response = await apiGet("UX_HTTP_API", "/esid-25-006/get-simulation-kpis-parquet", queryParams);
      
      // Handle NaN values in the response if it's a string
      if (typeof response === 'string') {
        response = response.replace(/:\s*NaN\s*([,}])/g, ': null$1');
        // Parse the cleaned string
        try {
          response = JSON.parse(response);
        } catch (error) {
          console.error("Error parsing cleaned response:", error);
        }
      }

      if (response && response.simulation && response.simulation.kpis) {
        // Extract the kpis object from the response
        const kpisData = response.simulation.kpis;
        // remove key "no BESS" if it exists
        if(kpisData && Object.keys(kpisData).includes("no BESS")) {
          delete kpisData["no BESS"];
        }
        setSimulationKpis(prev => ({ ...prev, [eventId]: kpisData }));
      } else {
        console.warn("No KPI data in response for event_id:", eventId);
        setSimulationKpis(prev => ({ ...prev, [eventId]: null }));
      }
    } catch (error) {
      console.error("Error fetching simulation KPIs:", error);
      setSnackbarMessage(`Error loading KPI data: ${error.message}`);
      setSnackbarOpen(true);
      setSimulationKpis(prev => ({ ...prev, [eventId]: null }));
    } finally {
      setLoadingKpis(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const fetchSimulationKpisForExport = async (eventId) => {
    const queryParams = {
      simulation_id: eventId
    };
    let s3_key = null;
    if (simulations.find(sim => sim.event_id === eventId) && simulations.find(sim => sim.event_id === eventId).message) {
      let cleanMessage = simulations.find(sim => sim.event_id === eventId).message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
      // Handle NaN values in JSON before parsing
      cleanMessage = cleanMessage.replace(/:\s*NaN\s*([,}])/g, ': null$1');
      let messageData = JSON.parse(cleanMessage);
      s3_key = messageData.s3_key;
    }
    if (s3_key) {
      queryParams.s3_key = s3_key;
    }
    let response = await apiGet("UX_HTTP_API", "/esid-25-006/get-simulation-kpis-parquet", queryParams);
    
    // Handle NaN values in the response string before returning
    if (typeof response === 'string') {
      response = response.replace(/:\s*NaN\s*([,}])/g, ': null$1');
    }
    
    return response;
  };

  const fetchSimulationInputFileForExport = async (eventId) => {
    const queryParams = {
      simulation_id: eventId
    };
    let s3_key = null;
    if (simulations.find(sim => sim.event_id === eventId) && simulations.find(sim => sim.event_id === eventId).message) {
      let cleanMessage = simulations.find(sim => sim.event_id === eventId).message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
      // Handle NaN values in JSON before parsing
      cleanMessage = cleanMessage.replace(/:\s*NaN\s*([,}])/g, ': null$1');
      let messageData = JSON.parse(cleanMessage);
      s3_key = messageData.s3_key;
    }
    if (s3_key) {
      queryParams.s3_key = s3_key;
    }
    let response = await apiGet("UX_HTTP_API", "/esid-25-006/get-simulation-input-file", queryParams);
    return response;
  };


  const fetchSimulationSettingsForExport = async (eventId) => {
    const queryParams = {
      simulation_id: eventId
    };
    let s3_key = null;
    if (simulations.find(sim => sim.event_id === eventId) && simulations.find(sim => sim.event_id === eventId).message) {
      let cleanMessage = simulations.find(sim => sim.event_id === eventId).message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
      // Handle NaN values in JSON before parsing
      cleanMessage = cleanMessage.replace(/:\s*NaN\s*([,}])/g, ': null$1');
      let messageData = JSON.parse(cleanMessage);
      s3_key = messageData.s3_key;
    }
    if (s3_key) {
      queryParams.s3_key = s3_key;
    }
    let response = await apiGet("UX_HTTP_API", "/esid-25-006/get-simulation-settings-parquet", queryParams);
    
    // Handle NaN values in the response string before returning
    if (typeof response === 'string') {
      response = response.replace(/:\s*NaN\s*([,}])/g, ': null$1');
    }
    
    return response;
  };
  // Filter and sort data based on search and filters
  const filteredData = useMemo(() => {
    return simulations
      .filter(item => {
        // Extract name from message if available
        let simulationName = item.name || "";
        
        if (item.message) {
          try {
            const messageData = JSON.parse(item.message);
            simulationName = messageData.name || item.name || "";
          } catch (error) {
            console.error("Error parsing simulation message:", error);
          }
        }
        
        const matchesSearch = (simulationName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.event_id || '').toLowerCase().includes(searchTerm.toLowerCase());

        // Date range filtering
        let matchesDateRange = true;
   

        return matchesSearch && matchesDateRange;
      })
      .sort((a, b) => {
        // Sort by created date with newest first
        const dateA = new Date(a.created || 0);
        const dateB = new Date(b.created || 0);
        return dateB - dateA;
      });
  }, [simulations, searchTerm]);

  const displayData = filteredData;

  // Handle row selection
  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === displayData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(displayData.map(item => item.event_id));
    }
  };

  // Handle row expansion
  const handleExpandRow = (eventId) => {
    const isExpanding = !expandedRows.includes(eventId);

    setExpandedRows(prev =>
      prev.includes(eventId)
        ? prev.filter(rowId => rowId !== eventId)
        : [...prev, eventId]
    );

    // Fetch KPI data when expanding
    if (isExpanding) {
      fetchSimulationKpis(eventId);
    }
  };

  // Handle configuration expansion
  const handleExpandConfig = (eventId, configName) => {
    const configKey = `${eventId}-${configName}`;
    setExpandedConfigs(prev =>
      prev.includes(configKey)
        ? prev.filter(key => key !== configKey)
        : [...prev, configKey]
    );
  };


  // Export function - Export selected rows only as Excel
  const exportToExcel = async () => {
    const selectedData = simulations.filter(item => selectedRows.includes(item.event_id));
    
    await exportSimulationsToExcel(
      selectedData,
      fetchSimulationKpisForExport,
      fetchSimulationSettingsForExport,
      fetchSimulationInputFileForExport,
      setSnackbarMessage,
      setSnackbarOpen
    );
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    if (!status) return theme.palette.grey[500];
    let statusLower = status.toLowerCase();
    switch (statusLower) {
      case "running":
      case "in progress":
        return "#FF9800"; // Orange for active
      case "completed":
        return "#4CAF50"; // Green for completed
      case "failed":
        return "#C62828"; // Red for failed
      case "error":
        return "#C62828"; // Red for error        
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box sx={{ padding: 4, marginLeft: 5, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="700" color="#2c3e50">
            BESS Simulations Overview
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={selectedRows.length === 0}
            sx={{
              background: selectedRows.length > 0
                ? `linear-gradient(135deg, ${colors.entras_green[100]} 0%, ${colors.entras_green[200]} 100%)`
                : '#e0e0e0',
              color: 'white',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: '600',
              px: 3,
              py: 1,
              boxShadow: selectedRows.length > 0
                ? `0 4px 12px ${colors.entras_green[100]}40`
                : 'none',
              '&:hover': {
                background: selectedRows.length > 0
                  ? `linear-gradient(135deg, ${colors.entras_green[200]} 0%, ${colors.entras_green[300]} 100%)`
                  : '#e0e0e0',
                transform: selectedRows.length > 0 ? 'translateY(-1px)' : 'none',
                boxShadow: selectedRows.length > 0
                  ? `0 6px 16px ${colors.entras_green[100]}60`
                  : 'none',
              },
              '&.Mui-disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {selectedRows.length > 0
              ? `Export to Excel (${selectedRows.length})`
              : 'Export to Excel (Select Rows)'}
          </Button>
        </Box>

        {/* Summary Cards */}


        {/* Filters and Search */}
        <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">

          <TextField
            size="small"
            placeholder="Search by simulation name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              minWidth: 350,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />

        </Box>
      </Box>

      {/* Selection Summary */}
      {selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: colors.entras_green[100] + '20',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `2px solid ${colors.entras_green[100]}`
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              icon={<TableChartIcon />}
              label={`${selectedRows.length} simulation${selectedRows.length > 1 ? 's' : ''} selected`}
              sx={{
                backgroundColor: colors.entras_green[100],
                color: 'white',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            />
            <Typography variant="body2" color="#555">
              Ready to export with all related KPI data
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setSelectedRows([])}
            sx={{
              color: colors.entras_green[200],
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: colors.entras_green[100] + '30',
              }
            }}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.length === displayData.length && displayData.length > 0}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < displayData.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Simulation</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Date Processed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingSimulations ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body1" color="#7f8c8d" sx={{ mt: 2 }}>
                      Loading simulations...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : simulationsError ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body1" color="error">
                      Error loading simulations: {simulationsError}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body1" color="#7f8c8d">
                      No simulations found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayData.map((item) => (
                  <React.Fragment key={item.event_id}>
                    <TableRow
                      hover
                      sx={{
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSelectRow(item.event_id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(item.event_id)}
                          onChange={() => handleSelectRow(item.event_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandRow(item.event_id);
                          }}
                        >
                          {expandedRows.includes(item.event_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="#2c3e50">
                            {(() => {
                              if (item.message) {
                                try {
                                  const messageData = JSON.parse(item.message);
                                  return messageData.name || item.name || "Unnamed Simulation";
                                } catch (error) {
                                  return item.name || "Unnamed Simulation";
                                }
                              }
                              return item.name || "Unnamed Simulation";
                            })()}
                          </Typography>
                          <Typography variant="caption" color="#7f8c8d" fontSize="0.75rem">
                            Event ID: {item.event_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(() => {
                            let status = item.status || "";
                            if (item.message) {
                              try {
                                const messageData = JSON.parse(item.message);
                                status = messageData.status || item.status || "";
                                status = status?.toLowerCase();
                              } catch (error) {
                                // Use original status if parsing fails
                              }
                            }
                            return status === "in progress" ? "In Progress" : status === "running" ? "Running" : status === "completed" ? "Completed" : status === "error" ? "Error" : status === "failed" ? "Failed" : "Unknown";
                          })()}
                          size="small"
                          sx={{
                            backgroundColor: (() => {
                              let status = item.status || "";
                              if (item.message) {
                                try {
                                  const messageData = JSON.parse(item.message);
                                  status = messageData.status || item.status || "";
                                } catch (error) {
                                  // Use original status if parsing fails
                                }
                              }
                              return getStatusColor(status);
                            })(),
                            color: 'white',
                            fontWeight: '500',
                            fontSize: '0.75rem',
                            padding: '4px',
                            width: '100px'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#2c3e50">
                          {item.created && item.created !== "LAST" ? (() => {
                            const date = new Date(item.created);
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            const hours = String(date.getHours()).padStart(2, '0');
                            const minutes = String(date.getMinutes()).padStart(2, '0');
                            return `${year}-${month}-${day} ${hours}:${minutes}`;
                          })() : item.created === "LAST" ? "Latest" : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Expandable row for KPI data */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ padding: 0, border: 0 }}>
                        <Collapse in={expandedRows.includes(item.event_id)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2, backgroundColor: '#f8f9fa', borderRadius: 2, p: 3 }}>
                            {loadingKpis[item.event_id] || (!simulationKpis[item.event_id] && simulationKpis[item.event_id] !== null) ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4, gap: 2 }}>
                                <CircularProgress />
                                <Typography variant="body1" color="#7f8c8d">
                                  Loading KPI data...
                                </Typography>
                              </Box>
                            ) : simulationKpis[item.event_id] && Object.keys(simulationKpis[item.event_id]).length > 0 ? (
                              <>
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, mb: 0 }}>
                                    Simulation KPI Data
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                      icon={<AssessmentIcon />}
                                      label={`${Object.keys(simulationKpis[item.event_id]).length} Configurations`}
                                      size="small"
                                      sx={{ backgroundColor: colors.entras_green[100], color: 'white', fontWeight: 600 }}
                                    />
                                  </Box>
                                </Box>

                                {/* Display KPI data in nested table structure */}
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                                  <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Expand</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Configuration / Period</TableCell>
                                        {/* Get KPI names from all configs - collect all unique KPI names */}
                                        {(() => {
                                          const allKpiNames = new Set();
                                          Object.values(simulationKpis[item.event_id] || {}).forEach(periods => {
                                            Object.values(periods).forEach(kpis => {
                                              Object.keys(kpis).forEach(kpiName => allKpiNames.add(kpiName));
                                            });
                                          });
                                          return Array.from(allKpiNames).sort().map(kpiName => (
                                            <TableCell key={kpiName} sx={{ fontWeight: 600 }} align="right">
                                              {kpiName}
                                            </TableCell>
                                          ));
                                        })()}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(() => {
                                        // Get all KPI names once for consistent column order
                                        const allKpiNames = new Set();
                                        Object.values(simulationKpis[item.event_id] || {}).forEach(periods => {
                                          Object.values(periods).forEach(kpis => {
                                            Object.keys(kpis).forEach(kpiName => allKpiNames.add(kpiName));
                                          });
                                        });
                                        const sortedKpiNames = Array.from(allKpiNames).sort();

                                        return Object.entries(simulationKpis[item.event_id] || {}).map(([config, periods]) => {
                                          const configKey = `${item.event_id}-${config}`;
                                          const aggregatedData = periods.aggregated || {};
                                          const isConfigExpanded = expandedConfigs.includes(configKey);

                                          return (
                                            <React.Fragment key={config}>
                                              {/* Configuration row with aggregated values */}
                                              <TableRow
                                                hover
                                                sx={{
                                                  backgroundColor: '#f8f9fa',
                                                  '&:hover': { backgroundColor: '#e9ecef' }
                                                }}
                                              >
                                                <TableCell>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleExpandConfig(item.event_id, config)}
                                                  >
                                                    {isConfigExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.entras_green[200] }}>
                                                    {config}
                                                  </Typography>
                                                </TableCell>
                                                {/* Display aggregated KPI values in consistent order */}
                                                {sortedKpiNames.map(kpiName => {
                                                  const kpiValue = aggregatedData[kpiName];
                                                  return (
                                                    <TableCell key={kpiName} align="right">
                                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                                        {typeof kpiValue === 'number'
                                                          ? (kpiName.includes('CAPEX') || kpiName.includes('NPV') || kpiName.includes('savings') || kpiName.includes('Cost')
                                                            ? formatCurrency(kpiValue)
                                                            : kpiValue.toFixed(2))
                                                          : kpiValue ?? '-'}
                                                        {kpiName === 'IRR' && typeof kpiValue === 'number' && '%'}
                                                      </Typography>
                                                    </TableCell>
                                                  );
                                                })}
                                              </TableRow>

                                              {/* Expandable period rows */}
                                              {/* order periods data acording to Period name "Period: 2025-02" this means Period: 2024-12 Period: 2025-01 these are year and mont info  */}


                                              {isConfigExpanded && Object.entries(periods)
                                                .sort(([periodA], [periodB]) => {
                                                  // Skip 'aggregated' in sorting
                                                  if (periodA === 'aggregated') return 1;
                                                  if (periodB === 'aggregated') return -1;
                                                  
                                                  // Parse periods in format "YYYY-MM" and sort chronologically
                                                  return periodA.localeCompare(periodB);
                                                })
                                                .map(([period, kpis]) => {
                                                if (period === 'aggregated') return null;

                                                return (
                                                  <TableRow key={`${config}-${period}`}>
                                                    <TableCell />
                                                    <TableCell sx={{ pl: 6 }}>
                                                      <Typography variant="body2" color="#7f8c8d">
                                                        Period: {period}
                                                      </Typography>
                                                    </TableCell>
                                                    {/* Display period KPI values in consistent order */}
                                                    {sortedKpiNames.map(kpiName => {
                                                      const kpiValue = kpis[kpiName];
                                                      return (
                                                        <TableCell key={kpiName} align="right">
                                                          <Typography variant="body2" color="#2c3e50">
                                                            {typeof kpiValue === 'number'
                                                              ? (kpiName.includes('CAPEX') || kpiName.includes('NPV') || kpiName.includes('savings') || kpiName.includes('Cost')
                                                                ? formatCurrency(kpiValue)
                                                                : kpiValue.toFixed(2))
                                                              : kpiValue ?? '-'}
                                                            {kpiName === 'IRR' && typeof kpiValue === 'number' && '%'}
                                                          </Typography>
                                                        </TableCell>
                                                      );
                                                    })}
                                                  </TableRow>
                                                );
                                              })}
                                            </React.Fragment>
                                          );
                                        });
                                      })()}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                                <Typography variant="body1" color="#7f8c8d">
                                  No KPI data available for this simulation.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>


      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
